import * as Dialog from "@radix-ui/react-dialog";
import { useForm, Controller } from "react-hook-form";
import { useState, useEffect } from "react";
import Button from "./button";
import Textarea from "./textarea";
import TagColorPicker from "./tag-color-picker";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TAG_COLORS, type TagColor } from "@/modules/tag/colors";

interface CreateTagModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: (newTagId?: string) => void;
	onSubmit: (data: TagFormValues) => Promise<string | void>;
	initialLabel?: string;
}

export type TagFormValues = z.infer<typeof tagFormSchema>;

const validTagColors = TAG_COLORS.map((c) => c.value) as [TagColor, ...TagColor[]];

const tagFormSchema = z.object({
	label: z.string().min(1, "Tag label is required").max(100, "Max 100 characters"),
	description: z.string().max(200, "Max 200 characters").nullable().optional(),
	color: z.enum(validTagColors, { message: "Please select a color" }),
});

export function CreateTagModal({ open, onOpenChange, onSuccess, onSubmit, initialLabel = "" }: CreateTagModalProps) {
	const [error, setError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		control,
		formState: { errors, isSubmitting },
	} = useForm<TagFormValues>({
		resolver: zodResolver(tagFormSchema),
		defaultValues: {
			label: initialLabel,
			description: "",
			color: undefined,
		},
	});

	// Update label and set random color when modal opens
	useEffect(() => {
		if (open) {
			if (initialLabel) {
				setValue("label", initialLabel);
			}
			// Set a random color
			const randomColor = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)].value;
			setValue("color", randomColor);
		}
	}, [open, initialLabel, setValue]);

	const handleFormSubmit = async (data: TagFormValues) => {
		setError(null);
		try {
			const newTagId = await onSubmit(data);
			onSuccess(newTagId || undefined);
			handleReset();
			onOpenChange(false);
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred while creating the tag.");
		}
	};

	const handleReset = () => {
		reset();
		setError(null);
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			handleReset();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog.Root open={open} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-lg modal-content overflow-hidden">
					<form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col max-h-[80vh]">
						{/* Header */}
						<div className="px-6 pt-6 pb-4 border-b border-gray-200">
							<Dialog.Title className="text-base font-semibold text-gray-900 leading-base">Create Tag</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 leading-sm mt-1">Enter tag details</Dialog.Description>
						</div>

						{/* Content */}
						<div className="overflow-y-auto px-6 py-4">
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-primary mb-1.5">
										Label <span className="text-red-500">*</span>
									</label>
									<div className="flex items-start gap-3">
										<div className="flex-1">
											<input
												id="tag-label"
												type="text"
												placeholder="Enter tag label"
												disabled={isSubmitting}
												className={`w-full px-4 py-2 border rounded-lg text-sm font-sans transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white ${errors.label ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1`}
												{...register("label")}
											/>
										</div>
										<Controller
											name="color"
											control={control}
											render={({ field }) => (
												<TagColorPicker value={field.value} onChange={field.onChange} disabled={isSubmitting} error={!!errors.color} />
											)}
										/>
									</div>
									{(errors.label || errors.color) && (
										<p className="text-sm text-red-500 mt-1">{errors.label?.message || errors.color?.message}</p>
									)}
								</div>
								<Textarea
									id="tag-description"
									label="Description"
									placeholder="Enter tag description (optional)"
									className="h-24"
									disabled={isSubmitting}
									error={errors.description?.message}
									{...register("description")}
								/>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
							<Dialog.Close asChild>
								<Button type="button" variant="soft">
									Cancel
								</Button>
							</Dialog.Close>
							<div className="flex items-center gap-3">
								{error && <p className="text-sm text-red-600 leading-sm">{error}</p>}
								<Button type="submit" variant="brand" disabled={isSubmitting}>
									{isSubmitting ? "Creating..." : "Create Tag"}
								</Button>
							</div>
						</div>
					</form>

					<Dialog.Close asChild>
						<button className="absolute top-7 right-6 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
